import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { HostRoutes } from './app/routes/HostRoutes';
import Login from './features/auth/pages/Login';
import AdminLogin from './features/auth/pages/AdminLogin';
import SignUp from './features/auth/pages/SignUp';
import ResetPassword from './features/auth/pages/ResetPassword';
import SelectRole from './features/auth/pages/SelectRole';
import Home from './pages/Home';
import PrivacyPolicy from './pages/PrivacyPolicy';
import { RequireAuth } from './app/guards/RequireAuth';
import ScrollToTop from './components/ScrollToTop';
import Approvals from './features/admin/pages/Approvals';

// Component to handle routing logic
const AppRouter = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Home />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      
      {/* Role selection - redirect to dashboard since this is host app */}
      <Route 
        path="/select-role" 
        element={
          user ? (
            <Navigate to="/host/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        } 
      />

      {/* Auth routes */}
      <Route 
        path="/login" 
        element={
          !user ? (
            <Login />
          ) : (
            <Navigate to="/host/dashboard" replace />
          )
        } 
      />
      {/* Admin login - allow access even when authenticated (for impersonation) */}
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route 
        path="/signup" 
        element={
          !user ? (
            <SignUp />
          ) : (
            <Navigate to="/host/dashboard" replace />
          )
        } 
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Secret approvals route */}
      <Route 
        path="/approvals" 
        element={
          <RequireAuth>
            <Approvals />
          </RequireAuth>
        } 
      />

      {/* Host routes - only for hosts */}
      <Route path="/host/*" element={<HostRoutes />} />

      {/* Catch all - redirect to home */}
      <Route 
        path="*" 
        element={<Navigate to="/" replace />} 
      />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </Router>
  );
}

export default App;
